import React, { useState, useEffect, useCallback } from 'react';
import api, { CrossDomainAttack } from '../api';
import { Activity, AlertTriangle, CheckCircle, Zap, LogOut, Bell, Settings, X, ChevronDown, RefreshCw, Shield, RotateCcw, DollarSign, ChevronRight, Link2 } from 'lucide-react';
import ConnectedSources from './ConnectedSources';
import ThreatFeed from './ThreatFeed';
import AIPipeline from './AIPipeline';
import IncidentDetail from './IncidentDetail';
import Framework5D from './Framework5D';
import CrossDomainIncidentDetail, { CrossDomainIncident } from './CrossDomainIncidentDetail';
import SettingsPage from './SettingsPage';
import NotificationsPage from './NotificationsPage';
import SearchBar from './SearchBar';
import SearchResultsPage from './SearchResultsPage';
import ThreatIntelPanel from './ThreatIntelPanel';
import ROIPage from './ROIPage';
import StatsCard from './StatsCard';
import RealAttackPanel from './RealAttackPanel';

const AWS_ATTACK_TEMPLATES: Record<string, { type: string; severity: 'critical' | 'high' | 'medium' | 'low'; title: string; description: string }> = {
  'ec2-crypto': {
    type: 'CryptoCurrency:EC2/BitcoinTool.B!DNS',
    severity: 'critical',
    title: 'EC2 Crypto Mining Detected',
    description: 'EC2 instance communicating with known Bitcoin mining pools'
  },
  'ec2-trojan': {
    type: 'Backdoor:EC2/C&CActivity.B!DNS',
    severity: 'critical',
    title: 'EC2 Trojan/Backdoor Detected',
    description: 'EC2 instance communicating with known command & control servers'
  },
  's3-exposed': {
    type: 'Policy:S3/BucketPublicAccessGranted',
    severity: 'high',
    title: 'S3 Bucket Publicly Exposed',
    description: 'Sensitive S3 bucket has been made publicly accessible'
  },
  'iam-credential': {
    type: 'UnauthorizedAccess:IAMUser/InstanceCredentialExfiltration.OutsideAWS',
    severity: 'critical',
    title: 'IAM Credential Exfiltration',
    description: 'AWS credentials used from IP address outside of AWS infrastructure'
  },
  'ssh-bruteforce': {
    type: 'UnauthorizedAccess:EC2/SSHBruteForce',
    severity: 'high',
    title: 'SSH Brute Force Attack',
    description: 'Multiple failed SSH login attempts detected on EC2 instance'
  },
  'port-scan': {
    type: 'Recon:EC2/Portscan',
    severity: 'medium',
    title: 'Port Scan Detected',
    description: 'EC2 instance performing outbound port scans to remote hosts'
  },
  'ddos': {
    type: 'Backdoor:EC2/DenialOfService.Tcp',
    severity: 'critical',
    title: 'DDoS Attack Participation',
    description: 'EC2 instance participating in TCP-based denial of service attack'
  },
  'dns-exfil': {
    type: 'Trojan:EC2/DNSDataExfiltration',
    severity: 'high',
    title: 'DNS Data Exfiltration',
    description: 'EC2 instance exfiltrating data through DNS queries'
  }
};

const AWS_ATTACK_OPTIONS = [
  { id: 'ec2-crypto', label: 'EC2 Crypto Mining' },
  { id: 'ec2-trojan', label: 'EC2 Trojan/Backdoor' },
  { id: 's3-exposed', label: 'S3 Bucket Exposed' },
  { id: 'iam-credential', label: 'IAM Credential Leak' },
  { id: 'ssh-bruteforce', label: 'SSH Brute Force' },
  { id: 'port-scan', label: 'Port Scan/Recon' },
  { id: 'ddos', label: 'DDoS Attack' },
  { id: 'dns-exfil', label: 'DNS Exfiltration' }
];

const OKTA_ATTACK_TEMPLATES: Record<string, { type: string; severity: 'critical' | 'high' | 'medium' | 'low'; title: string; description: string }> = {
  'brute-force': {
    type: 'security.threat.detected',
    severity: 'critical',
    title: 'Brute Force Login Attack',
    description: 'Multiple failed authentication attempts detected from suspicious IP'
  },
  'credential-stuffing': {
    type: 'user.account.credential_stuffing',
    severity: 'critical',
    title: 'Credential Stuffing Attack',
    description: 'Automated credential testing detected across multiple accounts'
  },
  'session-hijack': {
    type: 'user.session.hijack',
    severity: 'critical',
    title: 'Session Hijacking Detected',
    description: 'User session accessed from anomalous location/device'
  },
  'mfa-bypass': {
    type: 'user.mfa.factor.bypass',
    severity: 'critical',
    title: 'MFA Bypass Attempt',
    description: 'Attempted authentication bypass of multi-factor authentication'
  },
  'phishing': {
    type: 'security.threat.phishing',
    severity: 'high',
    title: 'Phishing Attack Detected',
    description: 'User clicked suspicious link in phishing email'
  },
  'privilege-escalation': {
    type: 'user.account.privilege_grant',
    severity: 'high',
    title: 'Unauthorized Privilege Escalation',
    description: 'User granted admin privileges outside normal workflow'
  },
  'impossible-travel': {
    type: 'user.session.impossible_travel',
    severity: 'high',
    title: 'Impossible Travel Detected',
    description: 'User authenticated from geographically impossible locations'
  },
  'account-takeover': {
    type: 'user.account.takeover',
    severity: 'critical',
    title: 'Account Takeover Detected',
    description: 'Account credentials changed after suspicious authentication'
  }
};

const OKTA_ATTACK_OPTIONS = [
  { id: 'brute-force', label: 'Brute Force Attack' },
  { id: 'credential-stuffing', label: 'Credential Stuffing' },
  { id: 'session-hijack', label: 'Session Hijacking' },
  { id: 'mfa-bypass', label: 'MFA Bypass Attempt' },
  { id: 'phishing', label: 'Phishing Attack' },
  { id: 'privilege-escalation', label: 'Privilege Escalation' },
  { id: 'impossible-travel', label: 'Impossible Travel' },
  { id: 'account-takeover', label: 'Account Takeover' }
];

const CS_ATTACK_OPTIONS = [
  { id: "ransomware", label: "Ransomware Execution" },
  { id: "mimikatz", label: "Mimikatz Credential Theft" },
  { id: "trojan", label: "Trojan/Backdoor" },
  { id: "psexec", label: "Lateral Movement (PSExec)" },
  { id: "process-hollowing", label: "Process Hollowing" },
  { id: "powershell", label: "Malicious PowerShell" },
  { id: "keylogger", label: "Keylogger Activity" },
  { id: "cobalt-strike", label: "Cobalt Strike Beacon" }
];

const CS_ATTACK_TEMPLATES: Record<string, { type: string; severity: "critical" | "high" | "medium" | "low"; title: string; description: string }> = {
  "ransomware": { type: "Malware:Ransomware", severity: "critical", title: "Ransomware Execution Blocked", description: "Suspicious encryption behavior detected on endpoint" },
  "mimikatz": { type: "CredentialAccess:Mimikatz", severity: "critical", title: "Mimikatz Credential Theft", description: "LSASS memory access and credential dumping detected" },
  "trojan": { type: "Malware:Trojan", severity: "high", title: "Trojan Horse Detected", description: "Malicious executable with C2 communication found" },
  "psexec": { type: "LateralMovement:PSExec", severity: "high", title: "Lateral Movement via PSExec", description: "Remote execution detected spreading to multiple hosts" },
  "process-hollowing": { type: "DefenseEvasion:ProcessHollowing", severity: "critical", title: "Process Hollowing Detected", description: "Malicious code injection via process hollowing technique" },
  "powershell": { type: "Execution:PowerShell", severity: "high", title: "Malicious PowerShell Execution", description: "Encoded/obfuscated PowerShell command execution" },
  "keylogger": { type: "Collection:Keylogger", severity: "high", title: "Keylogger Activity Detected", description: "Keystroke logging malware capturing user input" },
  "cobalt-strike": { type: "C2:CobaltStrike", severity: "critical", title: "Cobalt Strike Beacon Detected", description: "Known red team/APT C2 framework beacon identified" }
};

const CROWDSTRIKE_THREAT_TEMPLATES = [
  { type: "Malware:Ransomware", severity: "critical" as const, title: "Ransomware Execution Blocked", description: "Suspicious encryption behavior detected on endpoint" },
  { type: "CredentialAccess:Mimikatz", severity: "critical" as const, title: "Mimikatz Credential Theft Detected", description: "LSASS memory access detected" },
  { type: "Malware:Trojan", severity: "high" as const, title: "Trojan Horse Detected", description: "Malicious executable found on endpoint" },
  { type: "LateralMovement:PSExec", severity: "high" as const, title: "Lateral Movement via PSExec", description: "Remote execution detected to multiple hosts" },
  { type: "Malware:Backdoor", severity: "critical" as const, title: "Backdoor Installation Detected", description: "Persistent backdoor mechanism installed" },
  { type: "DefenseEvasion:ProcessHollowing", severity: "critical" as const, title: "Process Hollowing Detected", description: "Malicious code injection via process hollowing" },
  { type: "Execution:PowerShell", severity: "high" as const, title: "Malicious PowerShell Execution", description: "Encoded PowerShell command execution detected" },
  { type: "Collection:Keylogger", severity: "high" as const, title: "Keylogger Activity Detected", description: "Keystroke logging malware identified" }
];

const OKTA_THREAT_TEMPLATES = [
  { type: "Authentication:BruteForce", severity: "high" as const, title: "Brute Force Login Attempt", description: "Multiple failed login attempts detected" },
  { type: "Authentication:SuspiciousLocation", severity: "high" as const, title: "Login from Suspicious Location", description: "Successful login from high-risk country" },
  { type: "PrivilegeEscalation:AdminGrant", severity: "critical" as const, title: "Unauthorized Admin Privilege Grant", description: "User granted super admin privileges" },
  { type: "Session:Hijacking", severity: "critical" as const, title: "Session Token Reuse Detected", description: "Session token used from different IP/device" },
  { type: "Authentication:ImpossibleTravel", severity: "high" as const, title: "Impossible Travel Detected", description: "Login from geographically impossible location" },
  { type: "Policy:MFADisabled", severity: "high" as const, title: "MFA Disabled for User", description: "Multi-factor authentication disabled" },
  { type: "Authentication:PasswordSpray", severity: "high" as const, title: "Password Spray Attack", description: "Single password attempted against multiple accounts" },
  { type: "DataAccess:BulkDownload", severity: "critical" as const, title: "Bulk Data Download Detected", description: "Unusual volume of data accessed" }
];

const transformCrowdStrikeAlert = (alert: any): Incident => {
  const severityMap: Record<string, Incident['severity']> = {
    'Critical': 'critical',
    'High': 'high', 
    'Medium': 'medium',
    'Low': 'low',
    'Informational': 'low'
  };
  
  return {
    id: `cs-real-${alert.composite_id || alert.id}`,
    source: 'crowdstrike',
    type: alert.tactic || alert.technique || 'Unknown',
    severity: severityMap[alert.severity_name] || 'medium',
    title: alert.display_name || alert.name || 'CrowdStrike Detection',
    description: alert.description || `${alert.tactic}: ${alert.technique}`,
    timestamp: new Date(alert.timestamp || alert.created_timestamp),
    status: alert.status === 'closed' ? 'remediated' : 'detected',
    resource: alert.device?.hostname || 'Unknown Host',
    mitre: alert.mitre_attack?.[0] ? {
      tactic: alert.mitre_attack[0].tactic,
      technique: alert.mitre_attack[0].technique,
      id: alert.mitre_attack[0].technique_id
    } : undefined,
    raw: alert
  };
};

export interface Incident {
  id: string;
  source: 'aws' | 'crowdstrike' | 'okta';
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timestamp: Date;
  status: 'detected' | 'analyzing' | 'remediated';
  resource?: string;
  mitre?: {
    tactic: string;
    technique: string;
    id: string;
  };
  raw?: any;
  isCrossDomain?: boolean;
  crossDomainData?: CrossDomainIncident;
}

interface User {
  username: string;
  email?: string;
}

interface DashboardProps {
  user: User;
  onSignOut: () => void;
}

const generateAWSDetails = () => {
  const instanceIds = ['i-0a1b2c3d4e5f67890', 'i-1b2c3d4e5f678901', 'i-2c3d4e5f67890123', 'i-3d4e5f6789012345'];
  const bucketNames = ['company-confidential-data', 'prod-customer-pii', 'internal-financials', 'backup-sensitive-docs'];
  const userNames = ['admin-user', 'deploy-service', 'data-pipeline', 'ci-cd-runner'];
  const regions = ['us-east-1', 'us-west-2', 'eu-west-1'];
  const ips = ['185.143.223.47', '91.234.99.42', '45.155.205.99', '194.26.29.113', '103.75.201.88'];
  
  return {
    instanceId: instanceIds[Math.floor(Math.random() * instanceIds.length)],
    bucketName: bucketNames[Math.floor(Math.random() * bucketNames.length)],
    userName: userNames[Math.floor(Math.random() * userNames.length)],
    region: regions[Math.floor(Math.random() * regions.length)],
    suspiciousIp: ips[Math.floor(Math.random() * ips.length)],
    accountId: '254886179156'
  };
};

const generateOktaDetails = () => {
  const userNames = ['john.smith@company.com', 'sarah.jones@company.com', 'mike.wilson@company.com', 'admin@company.com'];
  const appNames = ['Salesforce', 'AWS Console', 'GitHub Enterprise', 'Slack', 'Office 365'];
  const locations = ['New York, US', 'London, UK', 'Moscow, RU', 'Beijing, CN', 'São Paulo, BR'];
  const ips = ['185.143.223.47', '91.234.99.42', '45.155.205.99', '194.26.29.113', '103.75.201.88'];
  const devices = ['Unknown Windows Device', 'Linux Server', 'MacBook Pro', 'iPhone 15', 'Android Device'];
  
  return {
    userName: userNames[Math.floor(Math.random() * userNames.length)],
    appName: appNames[Math.floor(Math.random() * appNames.length)],
    location: locations[Math.floor(Math.random() * locations.length)],
    suspiciousIp: ips[Math.floor(Math.random() * ips.length)],
    device: devices[Math.floor(Math.random() * devices.length)],
    failedAttempts: Math.floor(Math.random() * 50 + 10)
  };
};

function generateThreatBatch(): Incident[] {
  const now = Date.now();
  const threats: Incident[] = [];
  
  const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);
  
  const awsKeys = shuffleArray(Object.keys(AWS_ATTACK_TEMPLATES)).slice(0, 4);
  awsKeys.forEach((key, i) => {
    const t = AWS_ATTACK_TEMPLATES[key];
    const details = generateAWSDetails();
    threats.push({
      id: `aws-${now}-${i}`,
      source: 'aws',
      type: t.type,
      severity: t.severity,
      title: t.title,
      description: `${t.description} (Instance: ${details.instanceId}, Region: ${details.region})`,
      timestamp: new Date(now - i * 60000),
      status: 'detected'
    });
  });
  
  const csTemplates = shuffleArray(CROWDSTRIKE_THREAT_TEMPLATES).slice(0, 4);
  const hostnames = ["WORKSTATION", "LAPTOP", "SERVER", "DC-PRIMARY", "WEB-SERVER"];
  csTemplates.forEach((t, i) => {
    threats.push({
      id: `cs-${now}-${i}`,
      source: 'crowdstrike',
      type: t.type,
      severity: t.severity,
      title: t.title,
      description: `${t.description} (${hostnames[Math.floor(Math.random() * hostnames.length)]}-${Math.floor(Math.random() * 100)})`,
      timestamp: new Date(now - i * 60000),
      status: 'detected'
    });
  });
  
  const oktaTemplates = shuffleArray(OKTA_THREAT_TEMPLATES).slice(0, 4);
  const users = ["admin@company.com", "john.smith@company.com", "sarah.chen@company.com", "mike.jones@company.com", "cfo@company.com"];
  const countries = ["Russia", "China", "North Korea", "Iran", "Unknown VPN"];
  oktaTemplates.forEach((t, i) => {
    threats.push({
      id: `okta-${now}-${i}`,
      source: 'okta',
      type: t.type,
      severity: t.severity,
      title: t.title,
      description: `${t.description} (User: ${users[Math.floor(Math.random() * users.length)]}, Location: ${countries[Math.floor(Math.random() * countries.length)]})`,
      timestamp: new Date(now - i * 60000),
      status: 'detected'
    });
  });
  
  return threats;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onSignOut }) => {
  const [activeView, setActiveView] = useState<'dashboard' | 'incident' | 'crossdomain' | 'settings' | 'notifications' | 'search' | 'roi'>('dashboard');
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [selectedCrossDomainIncident, setSelectedCrossDomainIncident] = useState<CrossDomainIncident | null>(null);
  const [demoMode, setDemoMode] = useState(true);
  const [threatStats, setThreatStats] = useState({ active: 0, remediated: 0, total: 0 });
  const [selectedStat, setSelectedStat] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [incidents, setIncidents] = useState<Incident[]>(() => {
    const saved = localStorage.getItem('arbitium-incidents');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map((i: any) => ({ ...i, timestamp: new Date(i.timestamp) }));
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sourceFilter, setSourceFilter] = useState<'all' | 'aws' | 'crowdstrike' | 'okta'>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [notification, setNotification] = useState<string | null>(null);
  const [show5DFramework, setShow5DFramework] = useState(false);
  const [showThreatIntel, setShowThreatIntel] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showAWSSubmenu, setShowAWSSubmenu] = useState(false);
  const [selectedAWSAttacks, setSelectedAWSAttacks] = useState<Set<string>>(new Set());
  const [showOktaSubmenu, setShowOktaSubmenu] = useState(false);
  const [selectedOktaAttacks, setSelectedOktaAttacks] = useState<Set<string>>(new Set());
  const [showCSSubmenu, setShowCSSubmenu] = useState(false);
  const [selectedCSAttacks, setSelectedCSAttacks] = useState<Set<string>>(new Set());
  const [showSimulatedSubmenu, setShowSimulatedSubmenu] = useState(false);
  const actionsRef = React.useRef<HTMLDivElement>(null);
  const [showRealAttackPanel, setShowRealAttackPanel] = useState(false);

  const triggerThreats = useCallback(() => {
    const newThreats = generateThreatBatch();
    setIncidents(prev => [...newThreats, ...prev]);
  }, []);

  const markAsRemediated = useCallback((id: string) => {
    setIncidents(prev => prev.map(i => 
      i.id === id ? { ...i, status: 'remediated' as const } : i
    ));
  }, []);

  const launchCrossDomainAttack = useCallback(async () => {
    setShowActionsDropdown(false);
    setNotification('🔗 Launching cross-domain attack simulation...');
    
    try {
      const attackData = await api.crossDomain.generate();
      
      const crossDomainIncident: CrossDomainIncident = {
        ...attackData,
        status: 'detected'
      };
      
      const summaryIncident: Incident = {
        id: attackData.id,
        source: 'aws',
        type: 'Cross-Domain:CoordinatedAttack',
        severity: 'critical',
        title: `🔗 ${attackData.scenario.name}`,
        description: `Coordinated attack detected across Okta, AWS, and CrowdStrike. Attacker IP: ${attackData.context.attackerIP}, Target: ${attackData.context.targetUser}. ${attackData.correlation.confidence}% correlation confidence.`,
        timestamp: new Date(),
        status: 'detected',
        isCrossDomain: true,
        crossDomainData: crossDomainIncident
      };
      
      setIncidents(prev => [summaryIncident, ...prev]);
      setNotification(`✓ Cross-domain attack detected: ${attackData.scenario.name}`);
      
    } catch (error) {
      console.error('Failed to generate cross-domain attack:', error);
      setNotification('Failed to generate cross-domain attack');
    }
  }, []);

  const launchAWSAttack = useCallback(async () => {
    if (selectedAWSAttacks.size === 0) return;
    
    setShowActionsDropdown(false);
    setShowAWSSubmenu(false);
    
    const attackIds = Array.from(selectedAWSAttacks);
    setNotification(`Launching ${attackIds.length} AWS attack simulation${attackIds.length > 1 ? 's' : ''}...`);
    
    for (let i = 0; i < attackIds.length; i++) {
      const attackId = attackIds[i];
      const template = AWS_ATTACK_TEMPLATES[attackId];
      const details = generateAWSDetails();
      
      const incident: Incident = {
        id: `aws-attack-${Date.now()}-${i}`,
        source: 'aws',
        type: template.type,
        severity: template.severity,
        title: template.title,
        description: getAWSAttackDescription(attackId, template, details),
        timestamp: new Date(),
        status: 'detected',
        resource: attackId.startsWith('s3') ? details.bucketName : details.instanceId
      };
      
      setIncidents(prev => [incident, ...prev]);
      
      if (i < attackIds.length - 1) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    
    setSelectedAWSAttacks(new Set());
    setNotification(`✓ ${attackIds.length} AWS attack${attackIds.length > 1 ? 's' : ''} simulated`);
  }, [selectedAWSAttacks]);

  const launchOktaAttack = useCallback(async () => {
    if (selectedOktaAttacks.size === 0) return;
    
    setShowActionsDropdown(false);
    setShowOktaSubmenu(false);
    
    const attackIds = Array.from(selectedOktaAttacks);
    setNotification("Launching " + attackIds.length + " Okta attack simulation" + (attackIds.length > 1 ? "s" : "") + "...");
    
    for (let i = 0; i < attackIds.length; i++) {
      const attackId = attackIds[i];
      const template = OKTA_ATTACK_TEMPLATES[attackId];
      const details = generateOktaDetails();
      
      const incident: Incident = {
        id: "okta-attack-" + Date.now() + "-" + i,
        source: "okta",
        type: template.type,
        severity: template.severity,
        title: template.title,
        description: getOktaAttackDescription(attackId, template, details),
        timestamp: new Date(),
        status: "detected",
        resource: details.userName
      };
      
      setIncidents(prev => [incident, ...prev]);
      
      if (i < attackIds.length - 1) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    
    setSelectedOktaAttacks(new Set());
    setNotification("✓ " + attackIds.length + " Okta attack" + (attackIds.length > 1 ? "s" : "") + " simulated");
  }, [selectedOktaAttacks]);

  const generateCSDetails = () => ({
    hostname: ["WORKSTATION-" + Math.floor(Math.random() * 900 + 100), "DESKTOP-" + Math.random().toString(36).substring(2, 8).toUpperCase(), "SRV-PROD-" + Math.floor(Math.random() * 50)][Math.floor(Math.random() * 3)],
    userName: ["jsmith", "admin", "svc_backup", "developer1", "mwilson"][Math.floor(Math.random() * 5)],
    processName: ["cmd.exe", "powershell.exe", "rundll32.exe", "svchost.exe", "explorer.exe"][Math.floor(Math.random() * 5)],
    filePath: "C:\\Windows\\Temp\\" + Math.random().toString(36).substring(2, 10) + ".exe",
    sha256: Array.from({length: 64}, () => "0123456789abcdef"[Math.floor(Math.random() * 16)]).join("")
  });

  const getCSAttackDescription = (attackId: string, template: typeof CS_ATTACK_TEMPLATES[string], details: ReturnType<typeof generateCSDetails>) => {
    switch (attackId) {
      case "ransomware": return "Ransomware activity detected on " + details.hostname + ". Process " + details.processName + " attempting to encrypt files. User: " + details.userName;
      case "mimikatz": return "Credential dumping tool Mimikatz detected on " + details.hostname + ". LSASS memory access by " + details.processName + ". Immediate response required.";
      case "trojan": return "Trojan detected at " + details.filePath + " on " + details.hostname + ". SHA256: " + details.sha256.substring(0, 16) + "...";
      case "psexec": return "Lateral movement via PSExec from " + details.hostname + " targeting multiple internal hosts. User: " + details.userName;
      case "process-hollowing": return "Process hollowing technique detected on " + details.hostname + ". Malicious code injected into " + details.processName + ".";
      case "powershell": return "Obfuscated PowerShell execution on " + details.hostname + ". Base64 encoded command detected. User: " + details.userName;
      case "keylogger": return "Keylogger malware active on " + details.hostname + ". Capturing keystrokes from user " + details.userName + ".";
      case "cobalt-strike": return "Cobalt Strike beacon detected on " + details.hostname + ". C2 communication established. Immediate containment required.";
      default: return template.description + " on " + details.hostname;
    }
  };

  const launchCSAttack = useCallback(async () => {
    if (selectedCSAttacks.size === 0) return;
    setShowActionsDropdown(false);
    setShowCSSubmenu(false);
    setShowSimulatedSubmenu(false);
    const attackIds = Array.from(selectedCSAttacks);
    setNotification("Launching " + attackIds.length + " CrowdStrike attack simulation" + (attackIds.length > 1 ? "s" : "") + "...");
    for (let i = 0; i < attackIds.length; i++) {
      const attackId = attackIds[i];
      const template = CS_ATTACK_TEMPLATES[attackId];
      const details = generateCSDetails();
      const incident: Incident = {
        id: "cs-attack-" + Date.now() + "-" + i,
        source: "crowdstrike",
        type: template.type,
        severity: template.severity,
        title: template.title,
        description: getCSAttackDescription(attackId, template, details),
        timestamp: new Date(),
        status: "detected",
        resource: details.hostname
      };
      setIncidents(prev => [incident, ...prev]);
      if (i < attackIds.length - 1) { await new Promise(r => setTimeout(r, 2000)); }
    }
    setSelectedCSAttacks(new Set());
    setNotification("✓ " + attackIds.length + " CrowdStrike attack" + (attackIds.length > 1 ? "s" : "") + " simulated");
  }, [selectedCSAttacks]);

  const getOktaAttackDescription = (attackId: string, template: typeof OKTA_ATTACK_TEMPLATES[string], details: ReturnType<typeof generateOktaDetails>) => {
    switch (attackId) {
      case "brute-force":
        return "User " + details.userName + " experienced " + details.failedAttempts + " failed login attempts from IP " + details.suspiciousIp + " (" + details.location + "). Automated attack pattern detected.";
      case "credential-stuffing":
        return "Automated credential testing detected targeting " + details.userName + " and multiple other accounts from " + details.suspiciousIp + ". Known breached credential patterns identified.";
      case "session-hijack":
        return "Active session for " + details.userName + " accessed from new device (" + details.device + ") in " + details.location + ". Session token potentially compromised.";
      case "mfa-bypass":
        return "MFA bypass attempt detected for " + details.userName + " accessing " + details.appName + ". Attacker attempted to circumvent second factor authentication.";
      case "phishing":
        return "User " + details.userName + " clicked phishing link and entered credentials on fake " + details.appName + " login page. Credentials may be compromised.";
      case "privilege-escalation":
        return "User " + details.userName + " was granted Super Admin privileges outside of change management process. Unauthorized privilege elevation detected.";
      case "impossible-travel":
        return "User " + details.userName + " authenticated from " + details.location + " within 30 minutes of login from New York, US. Physically impossible travel detected.";
      case "account-takeover":
        return "Account " + details.userName + " password and MFA factors changed from " + details.suspiciousIp + " (" + details.location + ") following suspicious authentication.";
      default:
        return template.description;
    }
  };

  const getAWSAttackDescription = (attackId: string, template: typeof AWS_ATTACK_TEMPLATES[string], details: ReturnType<typeof generateAWSDetails>) => {
    switch (attackId) {
      case 'ec2-crypto':
        return `EC2 instance ${details.instanceId} in ${details.region} is querying cryptocurrency mining pool domains. Outbound traffic to mining pools detected from suspicious process.`;
      case 'ec2-trojan':
        return `EC2 instance ${details.instanceId} is communicating with known C&C server at ${details.suspiciousIp}. Backdoor activity detected with periodic beaconing behavior.`;
      case 's3-exposed':
        return `S3 bucket ${details.bucketName} in account ${details.accountId} has been configured with public access. Bucket contains potentially sensitive data.`;
      case 'iam-credential':
        return `IAM credentials for user ${details.userName} were used from external IP ${details.suspiciousIp} outside AWS infrastructure. Possible credential compromise.`;
      case 'ssh-bruteforce':
        return `EC2 instance ${details.instanceId} received ${Math.floor(Math.random() * 500 + 100)} failed SSH login attempts from ${details.suspiciousIp} in the last hour.`;
      case 'port-scan':
        return `EC2 instance ${details.instanceId} is performing port scans against external hosts. ${Math.floor(Math.random() * 1000 + 500)} connection attempts detected.`;
      case 'ddos':
        return `EC2 instance ${details.instanceId} is participating in DDoS attack. High volume TCP traffic detected to multiple external targets.`;
      case 'dns-exfil':
        return `EC2 instance ${details.instanceId} is making suspicious DNS queries with encoded data. Potential data exfiltration via DNS tunneling detected.`;
      default:
        return template.description;
    }
  };

  const toggleAWSAttack = (attackId: string) => {
    setSelectedAWSAttacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attackId)) {
        newSet.delete(attackId);
      } else {
        newSet.add(attackId);
      }
      return newSet;
    });
  };

  const toggleAllAWSAttacks = () => {
    if (selectedAWSAttacks.size === AWS_ATTACK_OPTIONS.length) {
      setSelectedAWSAttacks(new Set());
    } else {
      setSelectedAWSAttacks(new Set(AWS_ATTACK_OPTIONS.map(o => o.id)));
    }
  };

  const toggleOktaAttack = (attackId: string) => {
    setSelectedOktaAttacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attackId)) {
        newSet.delete(attackId);
      } else {
        newSet.add(attackId);
      }
      return newSet;
    });
  };

  const toggleAllOktaAttacks = () => {
    if (selectedOktaAttacks.size === OKTA_ATTACK_OPTIONS.length) {
      setSelectedOktaAttacks(new Set());
    } else {
      setSelectedOktaAttacks(new Set(OKTA_ATTACK_OPTIONS.map(o => o.id)));
    }
  };

  const toggleCSAttack = (attackId: string) => {
    setSelectedCSAttacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attackId)) {
        newSet.delete(attackId);
      } else {
        newSet.add(attackId);
      }
      return newSet;
    });
  };

  const toggleAllCSAttacks = () => {
    if (selectedCSAttacks.size === CS_ATTACK_OPTIONS.length) {
      setSelectedCSAttacks(new Set());
    } else {
      setSelectedCSAttacks(new Set(CS_ATTACK_OPTIONS.map(o => o.id)));
    }
  };

  useEffect(() => {
    localStorage.setItem('arbitium-incidents', JSON.stringify(incidents));
  }, [incidents]);

  useEffect(() => {
    const active = incidents.filter(i => i.status === 'detected').length;
    const remediated = incidents.filter(i => i.status === 'remediated').length;
    const total = incidents.length;
    setThreatStats({ active, remediated, total });
  }, [incidents]);

  useEffect(() => {
    const fetchRealDetections = async () => {
      if (!autoFetchEnabled) return;
      try {
        const csDetections = await api.crowdstrike.getDetections();
        if (csDetections && Array.isArray(csDetections) && csDetections.length > 0) {
          const realIncidents = csDetections.map(transformCrowdStrikeAlert);
          setIncidents(prev => {
            const existingIds = new Set(prev.map(i => i.id));
            const newReal = realIncidents.filter(i => !existingIds.has(i.id));
            if (newReal.length > 0) {
              return [...newReal, ...prev];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Failed to fetch CrowdStrike detections:', error);
      }
    };

    fetchRealDetections();
    const interval = setInterval(fetchRealDetections, 30000);
    return () => clearInterval(interval);
  }, [autoFetchEnabled]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowActionsDropdown(false);
        setShowAWSSubmenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeView !== 'dashboard') {
      setSelectedIds(new Set());
    }
  }, [activeView]);

  const triggerRealThreat = async () => {
    try {
      const compositeId = '818642fb219c4095b8f135cafe9f400f:ind:09b48602390a4c9ca3c93284dcb77a48:179964348-10418-298768';
      const result = await api.crowdstrike.reopenDetections([compositeId]);
      if (result.success) {
        setNotification('CrowdStrike detection reopened');
        setTimeout(() => fetchCSOnly(), 1000);
      } else {
        setNotification(result.error || 'Failed to reopen detection');
      }
    } catch (e) {
      console.error('Failed to trigger real threat:', e);
      setNotification('Failed to trigger real threat');
    }
  };

  const fetchCSOnly = async () => {
    try {
      const csDetections = await api.crowdstrike.getDetections();
      if (csDetections && Array.isArray(csDetections) && csDetections.length > 0) {
        const realIncidents = csDetections.map(transformCrowdStrikeAlert);
        const existingIds = new Set(incidents.map(i => i.id));
        const newReal = realIncidents.filter(i => !existingIds.has(i.id));
        
        if (newReal.length > 0) {
          setIncidents(prev => [...newReal, ...prev]);
          setSourceFilter('crowdstrike');
          setNotification(`Found ${newReal.length} new CrowdStrike detection(s)`);
        } else {
          setSourceFilter('crowdstrike');
          setNotification('No new CrowdStrike detections (showing existing)');
        }
      } else {
        setNotification('No CrowdStrike detections found');
      }
    } catch (error) {
      console.error('Failed to fetch CrowdStrike detections:', error);
      setNotification('Failed to fetch CrowdStrike detections');
    }
  };

  const closeSelected = async () => {
    if (selectedIds.size === 0) return;
    
    const csCompositeIds: string[] = [];
    const idsToRemove = new Set<string>();
    
    incidents.forEach(incident => {
      if (selectedIds.has(incident.id)) {
        idsToRemove.add(incident.id);
        if (incident.raw?.composite_id) {
          csCompositeIds.push(incident.raw.composite_id);
        }
      }
    });
    
    if (csCompositeIds.length > 0) {
      try {
        await api.crowdstrike.closeDetections(csCompositeIds);
      } catch (e) {
        console.error('Failed to close CS detections:', e);
      }
    }
    
    setIncidents(prev => prev.filter(i => !idsToRemove.has(i.id)));
    setSelectedIds(new Set());
    setShowActionsDropdown(false);
  };

  const resetAll = async () => {
    const csCompositeIds = incidents
      .filter(i => i.raw?.composite_id)
      .map(i => i.raw.composite_id);
    
    if (csCompositeIds.length > 0) {
      try {
        await api.crowdstrike.closeDetections(csCompositeIds);
      } catch (e) {
        console.error('Failed to close CS detections:', e);
      }
    }
    
    localStorage.removeItem('arbitium-incidents');
    setIncidents([]);
    setSelectedIds(new Set());
    setShowActionsDropdown(false);
  };

  const handleIncidentSelect = (incident: Incident) => {
    if (incident.isCrossDomain && incident.crossDomainData) {
      setSelectedCrossDomainIncident(incident.crossDomainData);
      setActiveView('crossdomain');
    } else {
      setSelectedIncident(incident);
      setActiveView('incident');
    }
  };

  const handleIncidentSelectById = (incidentId: string) => {
    const incident = incidents.find(i => i.id === incidentId);
    if (incident) {
      handleIncidentSelect(incident);
    }
  };

  const handleAutoRemediate = (incident: Incident) => {
    setSelectedIncident({ ...incident, status: 'analyzing' });
    setActiveView('incident');
  };

  const handleBackToDashboard = () => {
    setActiveView('dashboard');
    setSelectedIncident(null);
    setSelectedCrossDomainIncident(null);
  };

  useEffect(() => {
    if (demoMode) {
      setIsAutoGenerating(false);
      return;
    }
    
    setIsAutoGenerating(true);
    
    const doTrigger = () => {
      if (incidents.length >= 120) {
        setIsAutoGenerating(false);
        return;
      }
      triggerThreats();
    };
    
    doTrigger();
    
    const interval = setInterval(() => {
      if (incidents.length >= 120) {
        setIsAutoGenerating(false);
        clearInterval(interval);
        return;
      }
      doTrigger();
    }, 120000);
    
    return () => clearInterval(interval);
  }, [demoMode]);

  return (
    <div className="min-h-screen bg-arbitium-darker font-nunito">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-arbitium-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-arbitium-accent/5 rounded-full blur-3xl"></div>
      </div>

      <header className="relative z-[9999] bg-arbitium-dark/80 backdrop-blur-xl border-b border-arbitium-primary/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/logo.png" 
              alt="Arbitium" 
              className="h-8 cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={() => { setActiveView('dashboard'); setSelectedIncident(null); setSelectedCrossDomainIncident(null); }}
            />
          </div>
          
          <div className="flex-1 max-w-xl mx-8">
            <SearchBar 
              onSearch={(query) => {
                setSearchQuery(query);
                setActiveView('search');
              }}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-arbitium-dark/50 rounded-full px-2 py-1">
              <button onClick={() => setDemoMode(!demoMode)} className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                demoMode 
                  ? 'bg-yellow-500/20 text-yellow-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {demoMode ? '📋 Demo' : '🔴 Live'}
              </button>
            </div>

            {notification && (
              <div className="fixed top-20 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-arbitium-accent/90 text-white rounded-lg shadow-lg z-[10000] text-sm font-medium">
                {notification}
              </div>
            )}

            <div className="relative z-[9999]" ref={actionsRef}>
              <button 
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-arbitium-primary/20 hover:bg-arbitium-primary/30 text-arbitium-accent border border-arbitium-primary/30 rounded-lg font-medium text-sm transition-colors"
              >
                ⚡ Actions
                <ChevronDown className={`w-4 h-4 transition-transform ${showActionsDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showActionsDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-arbitium-dark border border-arbitium-primary/30 rounded-lg shadow-2xl z-[9999] overflow-visible">
                  <button
                    onClick={launchCrossDomainAttack}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-gradient-to-r hover:from-purple-500/20 hover:to-cyan-500/20 transition-colors border-b border-arbitium-primary/20"
                  >
                    <Link2 className="w-4 h-4 text-purple-400" />
                    <span className="font-medium">Cross-Domain Attack</span>
                  </button>
                  
                  <button
                    onClick={() => { setShowRealAttackPanel(true); setShowActionsDropdown(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-gradient-to-r hover:from-red-500/20 hover:to-orange-500/20 transition-colors border-b border-arbitium-primary/20"
                  >
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="font-medium">🎯 Real Attacks</span>
                  </button>
                  
                  {/* Simulated Attacks Submenu */}
                  <div 
                    className="relative"
                    onMouseEnter={() => setShowSimulatedSubmenu(true)}
                    onMouseLeave={() => { setShowSimulatedSubmenu(false); setShowAWSSubmenu(false); setShowCSSubmenu(false); setShowOktaSubmenu(false); }}
                  >
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors border-b border-arbitium-primary/20"
                    >
                      <span className="text-lg">📋</span>
                      <span className="font-medium">Simulated Attacks</span>
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </button>
                    
                    {showSimulatedSubmenu && (
                      <div className="absolute left-full top-0 ml-1 w-48 bg-arbitium-dark border border-arbitium-primary/30 rounded-lg shadow-2xl z-[10000]">
                        {/* AWS Attack Submenu */}
                        <div 
                          className="relative"
                          onMouseEnter={() => setShowAWSSubmenu(true)}
                          onMouseLeave={() => setShowAWSSubmenu(false)}
                        >
                          <button className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors">
                            <span className="text-lg">☁️</span>
                            AWS Attack
                            <ChevronRight className="w-4 h-4 ml-auto" />
                          </button>
                          
                          {showAWSSubmenu && (
                            <div className="absolute left-full top-0 ml-1 w-64 bg-arbitium-dark border border-arbitium-primary/30 rounded-lg shadow-2xl z-[10001]">
                              <button
                                onClick={toggleAllAWSAttacks}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors border-b border-arbitium-primary/20"
                              >
                                <div className={`w-4 h-4 rounded border ${selectedAWSAttacks.size === AWS_ATTACK_OPTIONS.length ? 'bg-arbitium-accent border-arbitium-accent' : 'border-gray-500'} flex items-center justify-center`}>
                                  {selectedAWSAttacks.size === AWS_ATTACK_OPTIONS.length && <CheckCircle className="w-3 h-3 text-white" />}
                                </div>
                                Select All
                              </button>
                              {AWS_ATTACK_OPTIONS.map(option => (
                                <button
                                  key={option.id}
                                  onClick={() => toggleAWSAttack(option.id)}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors"
                                >
                                  <div className={`w-4 h-4 rounded border ${selectedAWSAttacks.has(option.id) ? 'bg-arbitium-accent border-arbitium-accent' : 'border-gray-500'} flex items-center justify-center`}>
                                    {selectedAWSAttacks.has(option.id) && <CheckCircle className="w-3 h-3 text-white" />}
                                  </div>
                                  {option.label}
                                </button>
                              ))}
                              <div className="border-t border-arbitium-primary/20 p-2">
                                <button
                                  onClick={launchAWSAttack}
                                  disabled={selectedAWSAttacks.size === 0}
                                  className={`w-full py-2 rounded-lg font-medium text-sm transition-colors ${selectedAWSAttacks.size > 0 ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:opacity-90' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
                                >
                                  🚀 Launch Attack{selectedAWSAttacks.size > 0 && ` (${selectedAWSAttacks.size})`}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* CrowdStrike Attack Submenu */}
                        <div 
                          className="relative"
                          onMouseEnter={() => setShowCSSubmenu(true)}
                          onMouseLeave={() => setShowCSSubmenu(false)}
                        >
                          <button className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors">
                            <span className="text-lg">🦅</span>
                            CrowdStrike Attack
                            <ChevronRight className="w-4 h-4 ml-auto" />
                          </button>
                          
                          {showCSSubmenu && (
                            <div className="absolute left-full top-0 ml-1 w-64 bg-arbitium-dark border border-arbitium-primary/30 rounded-lg shadow-2xl z-[10001]">
                              <button
                                onClick={toggleAllCSAttacks}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors border-b border-arbitium-primary/20"
                              >
                                <div className={`w-4 h-4 rounded border ${selectedCSAttacks.size === CS_ATTACK_OPTIONS.length ? 'bg-arbitium-accent border-arbitium-accent' : 'border-gray-500'} flex items-center justify-center`}>
                                  {selectedCSAttacks.size === CS_ATTACK_OPTIONS.length && <CheckCircle className="w-3 h-3 text-white" />}
                                </div>
                                Select All
                              </button>
                              {CS_ATTACK_OPTIONS.map(option => (
                                <button
                                  key={option.id}
                                  onClick={() => toggleCSAttack(option.id)}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors"
                                >
                                  <div className={`w-4 h-4 rounded border ${selectedCSAttacks.has(option.id) ? 'bg-arbitium-accent border-arbitium-accent' : 'border-gray-500'} flex items-center justify-center`}>
                                    {selectedCSAttacks.has(option.id) && <CheckCircle className="w-3 h-3 text-white" />}
                                  </div>
                                  {option.label}
                                </button>
                              ))}
                              <div className="border-t border-arbitium-primary/20 p-2">
                                <button
                                  onClick={launchCSAttack}
                                  disabled={selectedCSAttacks.size === 0}
                                  className={`w-full py-2 rounded-lg font-medium text-sm transition-colors ${selectedCSAttacks.size > 0 ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:opacity-90' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
                                >
                                  🚀 Launch Attack{selectedCSAttacks.size > 0 && ` (${selectedCSAttacks.size})`}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Okta Attack Submenu */}
                        <div 
                          className="relative"
                          onMouseEnter={() => setShowOktaSubmenu(true)}
                          onMouseLeave={() => setShowOktaSubmenu(false)}
                        >
                          <button className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors">
                            <span className="text-lg">🔐</span>
                            Okta Attack
                            <ChevronRight className="w-4 h-4 ml-auto" />
                          </button>
                          
                          {showOktaSubmenu && (
                            <div className="absolute left-full top-0 ml-1 w-64 bg-arbitium-dark border border-arbitium-primary/30 rounded-lg shadow-2xl z-[10001]">
                              <button
                                onClick={toggleAllOktaAttacks}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors border-b border-arbitium-primary/20"
                              >
                                <div className={`w-4 h-4 rounded border ${selectedOktaAttacks.size === OKTA_ATTACK_OPTIONS.length ? 'bg-arbitium-accent border-arbitium-accent' : 'border-gray-500'} flex items-center justify-center`}>
                                  {selectedOktaAttacks.size === OKTA_ATTACK_OPTIONS.length && <CheckCircle className="w-3 h-3 text-white" />}
                                </div>
                                Select All
                              </button>
                              {OKTA_ATTACK_OPTIONS.map(option => (
                                <button
                                  key={option.id}
                                  onClick={() => toggleOktaAttack(option.id)}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors"
                                >
                                  <div className={`w-4 h-4 rounded border ${selectedOktaAttacks.has(option.id) ? 'bg-arbitium-accent border-arbitium-accent' : 'border-gray-500'} flex items-center justify-center`}>
                                    {selectedOktaAttacks.has(option.id) && <CheckCircle className="w-3 h-3 text-white" />}
                                  </div>
                                  {option.label}
                                </button>
                              ))}
                              <div className="border-t border-arbitium-primary/20 p-2">
                                <button
                                  onClick={launchOktaAttack}
                                  disabled={selectedOktaAttacks.size === 0}
                                  className={`w-full py-2 rounded-lg font-medium text-sm transition-colors ${selectedOktaAttacks.size > 0 ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
                                >
                                  🚀 Launch Attack{selectedOktaAttacks.size > 0 && ` (${selectedOktaAttacks.size})`}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => { triggerThreats(); setShowActionsDropdown(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-arbitium-primary/10 hover:text-white transition-colors"
                  >
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Trigger Threats
                  </button>

                  <button
                    onClick={closeSelected}
                    disabled={selectedIds.size === 0}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${selectedIds.size === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 hover:bg-arbitium-primary/10 hover:text-white'}`}
                  >
                    <X className="w-4 h-4 text-red-400" />
                    Close Selected {selectedIds.size > 0 && `(${selectedIds.size})`}
                  </button>
                  <div className="border-t border-arbitium-primary/20">
                    <button
                      onClick={resetAll}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4 text-red-400" />
                      Reset All
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => setShow5DFramework(!show5DFramework)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                show5DFramework 
                  ? "bg-arbitium-primary/20 text-arbitium-primary" 
                  : "text-gray-400 hover:text-white hover:bg-arbitium-primary/10"
              }`}
              title="5D AI Framework"
            >
              <span className="text-lg">🧠</span>
              <span className="text-sm">5D</span>
            </button>
            <button 
              onClick={() => setShowThreatIntel(!showThreatIntel)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                showThreatIntel 
                  ? "bg-arbitium-primary/20 text-arbitium-primary" 
                  : "text-gray-400 hover:text-white hover:bg-arbitium-primary/10"
              }`}
              title="Threat Intelligence"
            >
              <span className="text-lg">📡</span>
              <span className="text-sm">Intel</span>
            </button>
            <button 
              onClick={() => setActiveView('roi')}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              <span>💰</span>
              <span className="text-sm">ROI</span>
            </button>
            <button 
              onClick={() => setActiveView('notifications')}
              className="relative flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              <span>🔔</span>
              {threatStats.active > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full">
                  {threatStats.active > 99 ? '99+' : threatStats.active}
                </span>
              )}
            </button>
            <button 
              onClick={() => setActiveView('settings')}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              <span>⚙️</span>
            </button>
            <div className="flex items-center gap-2 pl-2 border-l border-gray-700">
              <span className="text-sm text-gray-400">{user.email || user.username}</span>
              <button 
                onClick={onSignOut}
                className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
                title="Exit"
              >
                <span>🚪</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 p-6">
        {activeView === 'dashboard' ? (
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 grid grid-cols-4 gap-4">
              <StatsCard 
                icon={<AlertTriangle className="w-6 h-6" />} 
                label="Active Threats" 
                value={threatStats.active.toString()} 
                trend={threatStats.active > 0 ? `${threatStats.active} detected` : "All clear"} 
                color={threatStats.active > 0 ? "red" : "green"} 
              />
              <StatsCard 
                icon={<CheckCircle className="w-6 h-6" />} 
                label="Remediated" 
                value={threatStats.remediated.toString()} 
                trend={`${threatStats.remediated} fixed`}
                color="green" 
              />
              <StatsCard 
                icon={<Activity className="w-6 h-6" />} 
                label="Total Threats" 
                value={threatStats.total.toString()} 
                trend="All sources" 
                color="purple" 
              />
              <StatsCard 
                icon={<Zap className="w-6 h-6" />} 
                label="Avg Response Time" 
                value="1.2s" 
                trend="-0.3s vs avg" 
                color="cyan" 
                onClick={() => setSelectedStat('Avg Response Time')} 
              />
            </div>

            <div className="col-span-12">
              <ConnectedSources />
            </div>
            <div className="col-span-12">
              <AIPipeline />
            </div>

            {show5DFramework && (
              <div className="col-span-12">
                <Framework5D aggregateMode={true} incidents={incidents} aggregateData={{ totalThreats: threatStats.total, activeThreats: threatStats.active, remediatedThreats: threatStats.remediated, sources: { aws: incidents.filter(i => i.source === "aws").length, crowdstrike: incidents.filter(i => i.source === "crowdstrike").length, okta: incidents.filter(i => i.source === "okta").length } }} />
              </div>
            )}

            {showThreatIntel && (
              <div className="col-span-12">
                <ThreatIntelPanel />
              </div>
            )}
            {showThreatIntel && (
              <div className="col-span-12">
                <ThreatIntelPanel />
              </div>
            )}
            {showThreatIntel && (
              <div className="col-span-12">
                <ThreatIntelPanel />
              </div>
            )}

            <div className="col-span-12">
              <ThreatFeed 
                onSelectIncident={handleIncidentSelect} 
                demoMode={demoMode}
                onStatsChange={(stats) => setThreatStats(stats)}
                incidents={incidents}
                setIncidents={setIncidents}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                sourceFilter={sourceFilter}
                setSourceFilter={setSourceFilter}
                severityFilter={severityFilter}
                setSeverityFilter={setSeverityFilter}
                onAutoRemediate={handleAutoRemediate}
              />
            </div>
          </div>
        ) : activeView === 'settings' ? (
          <SettingsPage onBack={handleBackToDashboard} />
        ) : activeView === 'notifications' ? (
          <NotificationsPage onBack={handleBackToDashboard} />
        ) : activeView === 'search' ? (
          <SearchResultsPage 
            query={searchQuery} 
            onBack={handleBackToDashboard}
            onSelectIncident={handleIncidentSelectById}
            incidents={incidents}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        ) : activeView === 'roi' ? (
          <ROIPage onBack={handleBackToDashboard} incidents={incidents} />
        ) : activeView === 'crossdomain' ? (
          selectedCrossDomainIncident && (
            <CrossDomainIncidentDetail 
              incident={selectedCrossDomainIncident}
              onBack={handleBackToDashboard}
              onRemediated={markAsRemediated}
            />
          )
        ) : (
          selectedIncident && (
            <IncidentDetail 
              onRemediated={markAsRemediated}
              incident={selectedIncident} 
              onBack={handleBackToDashboard}
              autoRemediate={selectedIncident.status === 'analyzing'}
            />
          )
        )}
      </main>


      {showRealAttackPanel && (
        <RealAttackPanel
          onClose={() => setShowRealAttackPanel(false)}
          onIncidentCreated={(incident) => {
            setIncidents(prev => [incident, ...prev]);
            setShowRealAttackPanel(false);
          }}
        />
      )}

      {selectedStat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedStat(null)}>
          <div className="bg-arbitium-dark border border-arbitium-primary/20 rounded-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{selectedStat}</h3>
              <button onClick={() => setSelectedStat(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-400">Detailed analytics coming soon...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
